import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllClients, getUpdateStatistics, getClientsWithIssues } from '../../utils/apiService';
import bastionAuth from '../../services/bastionAuthService';

const UpdateTracking = () => {
  const [clients, setClients] = useState([]);
  const [allUpdates, setAllUpdates] = useState([]);
  const [updateStats, setUpdateStats] = useState(null);
  const [clientIssues, setClientIssues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState({
    clientId: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    updateType: '',
    searchText: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [updatesPerPage] = useState(20);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Charger toutes les données en parallèle
      const [clientsData, statsData, issuesData] = await Promise.all([
        getAllClients(),
        getUpdateStatistics(),
        getClientsWithIssues()
      ]);

      setClients(clientsData);
      setUpdateStats(statsData);
      setClientIssues(issuesData);

      // Consolider toutes les mises à jour
      const consolidatedUpdates = [];
      clientsData.forEach(client => {
        if (client.updates && Array.isArray(client.updates)) {
          client.updates.forEach(update => {
            consolidatedUpdates.push({
              ...update,
              clientId: client.id,
              clientName: client.name,
              clientEnvironment: client.environment,
              slackChannelId: client.slackChannelId
            });
          });
        }
      });

      consolidatedUpdates.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAllUpdates(consolidatedUpdates);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour filtrer les mises à jour
  const getFilteredUpdates = () => {
    let filtered = [...allUpdates];

    if (filters.clientId) {
      filtered = filtered.filter(update => update.clientId.toString() === filters.clientId);
    }

    if (filters.status) {
      filtered = filtered.filter(update => update.status === filters.status);
    }

    if (filters.updateType) {
      filtered = filtered.filter(update => update.updateType === filters.updateType);
    }

    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      filtered = filtered.filter(update => new Date(update.date) >= dateFrom);
    }

    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      dateTo.setHours(23, 59, 59, 999);
      filtered = filtered.filter(update => new Date(update.date) <= dateTo);
    }

    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(update => 
        update.clientName.toLowerCase().includes(searchLower) ||
        update.title.toLowerCase().includes(searchLower) ||
        update.description.toLowerCase().includes(searchLower) ||
        update.ticketUrl?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  // Fonction pour trier les mises à jour
  const getSortedUpdates = (updates) => {
    return [...updates].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      if (sortConfig.key === 'date') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getNestedValue = (obj, key) => {
    return key.split('.').reduce((o, k) => (o || {})[k], obj) || '';
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'planned': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'completed': 'Terminée',
      'in_progress': 'En cours',
      'planned': 'Planifiée',
      'failed': 'Échouée',
      'cancelled': 'Annulée'
    };
    return statusLabels[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUpdatedComponents = (update) => {
    const components = [];
    if (update.commits?.manager) components.push(`Manager: ${update.commits.manager}`);
    if (update.commits?.marshallerFrontal) components.push(`Marshaller: ${update.commits.marshallerFrontal}`);
    if (update.commits?.marshallerInterop) components.push(`M-Interop: ${update.commits.marshallerInterop}`);
    if (update.commits?.patientApp) components.push(`Patient App: ${update.commits.patientApp}`);
    if (update.commits?.datamodel) components.push(`Datamodel: ${update.commits.datamodel}`);
    return components;
  };

  // Pagination
  const filteredAndSortedUpdates = getSortedUpdates(getFilteredUpdates());
  const totalPages = Math.ceil(filteredAndSortedUpdates.length / updatesPerPage);
  const indexOfLastUpdate = currentPage * updatesPerPage;
  const indexOfFirstUpdate = indexOfLastUpdate - updatesPerPage;
  const currentUpdates = filteredAndSortedUpdates.slice(indexOfFirstUpdate, indexOfLastUpdate);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Suivi des Mises à Jour</h1>
        <p className="text-gray-600">Vue d'ensemble complète des mises à jour et problèmes clients</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Tableau de bord
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tracking'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📋 Suivi détaillé
            </button>
            <button
              onClick={() => setActiveTab('issues')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'issues'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚠️ Problèmes détectés
              {clientIssues && (clientIssues.noProbesInstalled.length + clientIssues.dbNotOptimized.length + clientIssues.urlsNotAccessible.length) > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {clientIssues.noProbesInstalled.length + clientIssues.dbNotOptimized.length + clientIssues.urlsNotAccessible.length}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && updateStats && (
        <div className="space-y-6">
          {/* Statistiques rapides */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 p-6 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{updateStats.totalUpdates}</div>
              <div className="text-sm text-blue-800">Total mises à jour</div>
            </div>
            <div className="bg-green-50 p-6 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{updateStats.completedUpdates}</div>
              <div className="text-sm text-green-800">Terminées</div>
              <div className="text-xs text-green-600 mt-1">
                {updateStats.totalUpdates > 0 ? Math.round((updateStats.completedUpdates / updateStats.totalUpdates) * 100) : 0}% de succès
              </div>
            </div>
            <div className="bg-yellow-50 p-6 rounded-lg">
              <div className="text-3xl font-bold text-yellow-600">{updateStats.inProgressUpdates}</div>
              <div className="text-sm text-yellow-800">En cours</div>
            </div>
            <div className="bg-red-50 p-6 rounded-lg">
              <div className="text-3xl font-bold text-red-600">{updateStats.failedUpdates}</div>
              <div className="text-sm text-red-800">Échouées</div>
              <div className="text-xs text-red-600 mt-1">
                {updateStats.totalUpdates > 0 ? Math.round((updateStats.failedUpdates / updateStats.totalUpdates) * 100) : 0}% d'échecs
              </div>
            </div>
            <div className="bg-purple-50 p-6 rounded-lg">
              <div className="text-3xl font-bold text-purple-600">{updateStats.recentUpdates.length}</div>
              <div className="text-sm text-purple-800">Ce mois-ci</div>
            </div>
          </div>

          {/* Mises à jour récentes */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Mises à jour récentes (30 derniers jours)</h2>
            {updateStats.recentUpdates.length === 0 ? (
              <p className="text-gray-500">Aucune mise à jour récente</p>
            ) : (
              <div className="space-y-3">
                {updateStats.recentUpdates.slice(0, 10).map((update, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Link 
                          to={`/client/${update.clientId}`}
                          className="font-medium text-purple-600 hover:text-purple-800"
                        >
                          {update.clientName}
                        </Link>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(update.status)}`}>
                          {getStatusLabel(update.status)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {update.title} - {formatDate(update.date)}
                      </div>
                    </div>
                    {update.ticketUrl && (
                      <a 
                        href={update.ticketUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Voir ticket
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top clients par nombre de mises à jour */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Clients les plus actifs</h2>
            <div className="space-y-2">
              {Object.entries(updateStats.updatesByClient)
                .sort((a, b) => b[1].total - a[1].total)
                .slice(0, 10)
                .map(([clientName, stats]) => (
                  <div key={clientName} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="font-medium">{clientName}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">{stats.total} MAJ</span>
                      <div className="flex gap-1">
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {stats.completed} ✓
                        </span>
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          {stats.failed} ✗
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Composants les plus mis à jour */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Composants les plus mis à jour</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(updateStats.componentUpdates)
                .sort((a, b) => b[1] - a[1])
                .map(([component, count]) => (
                  <div key={component} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{count}</div>
                    <div className="text-sm text-gray-600">{component}</div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Tracking Tab - Le suivi détaillé avec filtres et tableau */}
      {activeTab === 'tracking' && (
        <div className="space-y-6">
          {/* Filtres */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Filtres</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={filters.clientId}
                  onChange={(e) => setFilters(prev => ({...prev, clientId: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Tous les clients</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="completed">Terminée</option>
                  <option value="in_progress">En cours</option>
                  <option value="planned">Planifiée</option>
                  <option value="failed">Échouée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={filters.updateType}
                  onChange={(e) => setFilters(prev => ({...prev, updateType: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Tous les types</option>
                  <option value="Production">Production</option>
                  <option value="Qualification">Qualification</option>
                  <option value="Développement">Développement</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({...prev, dateFrom: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({...prev, dateTo: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
                <input
                  type="text"
                  value={filters.searchText}
                  onChange={(e) => setFilters(prev => ({...prev, searchText: e.target.value}))}
                  placeholder="Client, titre, description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setFilters({
                  clientId: '',
                  status: '',
                  dateFrom: '',
                  dateTo: '',
                  updateType: '',
                  searchText: ''
                })}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Réinitialiser les filtres
              </button>
            </div>
          </div>

          {/* Tableau des mises à jour */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                Mises à jour ({filteredAndSortedUpdates.length} résultat{filteredAndSortedUpdates.length !== 1 ? 's' : ''})
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('clientName')}
                    >
                      Client {sortConfig.key === 'clientName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Titre
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      Statut {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Composants
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentUpdates.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                        Aucune mise à jour trouvée
                      </td>
                    </tr>
                  ) : (
                    currentUpdates.map((update, index) => (
                      <tr key={`${update.clientId}-${update.id}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(update.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <Link 
                            to={`/client/${update.clientId}`}
                            className="text-purple-600 hover:text-purple-800 hover:underline"
                          >
                            {update.clientName}
                          </Link>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="truncate" title={update.title}>
                            {update.title}
                          </div>
                          {update.description && (
                            <div className="text-xs text-gray-500 truncate" title={update.description}>
                              {update.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            update.updateType === 'Production' ? 'bg-red-100 text-red-800' :
                            update.updateType === 'Qualification' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {update.updateType || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(update.status)}`}>
                            {getStatusLabel(update.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="space-y-1">
                            {getUpdatedComponents(update).map((component, i) => (
                              <div key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                {component}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {update.ticketUrl ? (
                            <a 
                              href={update.ticketUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              Voir ticket
                            </a>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <Link 
                            to={`/client/${update.clientId}`}
                            className="text-purple-600 hover:text-purple-800"
                          >
                            Voir client
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Suivant
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Affichage de <span className="font-medium">{indexOfFirstUpdate + 1}</span> à{' '}
                      <span className="font-medium">{Math.min(indexOfLastUpdate, filteredAndSortedUpdates.length)}</span> sur{' '}
                      <span className="font-medium">{filteredAndSortedUpdates.length}</span> résultats
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Précédent
                      </button>
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Suivant
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Issues Tab - Les problèmes détectés */}
      {activeTab === 'issues' && clientIssues && (
        <div className="space-y-6">
          {/* Sondes non installées */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h2 className="text-xl font-semibold text-red-800">
                Clients sans sondes installées ({clientIssues.noProbesInstalled.length})
              </h2>
            </div>
            {clientIssues.noProbesInstalled.length === 0 ? (
              <p className="text-green-600">✓ Tous les clients ont des sondes installées</p>
            ) : (
              <div className="space-y-3">
                {clientIssues.noProbesInstalled.map(client => (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div>
                      <Link 
                        to={`/client/${client.id}`}
                        className="font-medium text-red-800 hover:text-red-900"
                      >
                        {client.name}
                      </Link>
                      <div className="text-sm text-red-600">
                        Environnement: {client.environment} • {client.serverCount} serveur(s)
                      </div>
                    </div>
                    <Link 
                      to={`/edit-client/${client.id}`}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      Corriger
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* BDD non optimisées */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              <h2 className="text-xl font-semibold text-orange-800">
                Clients avec BDD non optimisées ({clientIssues.dbNotOptimized.length})
              </h2>
            </div>
            {clientIssues.dbNotOptimized.length === 0 ? (
              <p className="text-green-600">✓ Toutes les bases de données sont optimisées</p>
            ) : (
              <div className="space-y-3">
                {clientIssues.dbNotOptimized.map(client => (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div>
                      <Link 
                        to={`/client/${client.id}`}
                        className="font-medium text-orange-800 hover:text-orange-900"
                      >
                        {client.name}
                      </Link>
                      <div className="text-sm text-orange-600">
                        Environnement: {client.environment} • {client.dbServers} serveur(s) BDD
                      </div>
                    </div>
                    <Link 
                      to={`/edit-client/${client.id}`}
                      className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                    >
                      Corriger
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* URLs non accessibles */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h2 className="text-xl font-semibold text-purple-800">
                Clients avec URLs non accessibles ({clientIssues.urlsNotAccessible.length})
              </h2>
            </div>
            {clientIssues.urlsNotAccessible.length === 0 ? (
              <p className="text-green-600">✓ Toutes les URLs sont accessibles de l'extérieur</p>
            ) : (
              <div className="space-y-3">
                {clientIssues.urlsNotAccessible.map(client => (
                  <div key={client.id} className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Link 
                        to={`/client/${client.id}`}
                        className="font-medium text-purple-800 hover:text-purple-900"
                      >
                        {client.name}
                      </Link>
                      <Link 
                        to={`/edit-client/${client.id}`}
                        className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                      >
                        Corriger
                      </Link>
                    </div>
                    <div className="text-sm text-purple-600">
                      Environnement: {client.environment}
                    </div>
                    <div className="mt-2 space-y-1">
                      {client.issues.map((issue, index) => (
                        <div key={index} className="text-xs bg-purple-100 px-2 py-1 rounded">
                          {issue}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateTracking;